import React, { useState } from 'react';
import { AccessibilityService } from '../../services/AccessibilityService';
import { HopeVoiceService } from '../../services/HopeVoiceService';

interface WelcomeIntakeProps {
  clientName?: string;
  onGetStarted: () => void;
}

const WelcomeIntake: React.FC<WelcomeIntakeProps> = ({
  clientName = "there",
  onGetStarted
}) => {
  const [readAloud, setReadAloud] = useState(HopeVoiceService.getSettings().enabled);
  const [largeText, setLargeText] = useState(
    AccessibilityService.getSettings().fontSize === 'large' || AccessibilityService.getSettings().fontSize === 'x-large'
  );
  const [highContrast, setHighContrast] = useState(AccessibilityService.isHighContrast());
  return (
    <div className="app">
      <div style={{
        maxWidth: '800px',
        margin: '0 auto',
        background: '#28527A',
        borderRadius: '16px',
        padding: '2rem',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
        marginTop: '2rem'
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h1 style={{ color: '#f9fafb', fontSize: '2rem', margin: '0 0 0.5rem 0' }}>
            Welcome to My ClinicCore
          </h1>
          <p style={{ color: '#6CA6C1', margin: 0 }}>Client Portal</p>
        </div>

        {/* Welcome Content */}
        <div style={{
          background: '#113F67',
          borderRadius: '12px',
          padding: '2rem',
          textAlign: 'left',
          border: '1px solid #6CA6C1'
        }}>
          <p style={{
            fontSize: '1.1rem',
            marginBottom: '1.5rem',
            color: '#f9fafb'
          }}>
            Hi {clientName},
          </p>

          <p style={{
            fontSize: '1rem',
            lineHeight: '1.6',
            marginBottom: '1.5rem',
            color: '#d1d5db'
          }}>
            This Client Portal will help us get started by making it easy for you to review our practice
            policies and provide some basic information before our first session.
          </p>

          <p style={{
            fontSize: '1rem',
            lineHeight: '1.6',
            marginBottom: '2.5rem',
            color: '#d1d5db'
          }}>
            If you leave the portal before completing everything, you can use the link we emailed to
            come back and start over. It should take between <strong style={{ color: '#f9fafb' }}>5-20 minutes</strong> to finish.
          </p>

          {/* Accessibility Preferences */}
          <div style={{
            background: '#1c2536',
            borderRadius: '10px',
            padding: '1.25rem',
            marginBottom: '2rem',
            border: '1px solid #374151'
          }}>
            <p style={{ color: '#f9fafb', fontWeight: '600', fontSize: '1rem', margin: '0 0 0.5rem 0' }}>
              Do you need help reading or navigating?
            </p>
            <p style={{ color: '#9ca3af', fontSize: '0.85rem', margin: '0 0 1rem 0' }}>
              Select any that apply — you can change these anytime.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.75rem' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                color: '#f9fafb',
                cursor: 'pointer',
                padding: '0.75rem',
                background: readAloud ? 'rgba(59, 130, 246, 0.15)' : '#374151',
                borderRadius: '8px',
                border: readAloud ? '1px solid #3b82f6' : '1px solid transparent',
                transition: 'all 0.2s'
              }}>
                <input
                  type="checkbox"
                  checked={readAloud}
                  onChange={(e) => {
                    setReadAloud(e.target.checked);
                    if (e.target.checked) {
                      HopeVoiceService.enable();
                      setTimeout(() => {
                        HopeVoiceService.speak(
                          `Hi ${clientName === 'there' ? '' : clientName}! Welcome to My ClinicCore. This portal will help you get started by reviewing practice policies and providing some basic information before your first session. Click Get Started when you're ready.`,
                          'polite'
                        );
                      }, 300);
                    } else {
                      HopeVoiceService.disable();
                    }
                  }}
                  style={{ marginRight: '0.75rem' }}
                />
                Read Aloud — Hope reads the screen for me
              </label>

              <label style={{
                display: 'flex',
                alignItems: 'center',
                color: '#f9fafb',
                cursor: 'pointer',
                padding: '0.75rem',
                background: largeText ? 'rgba(59, 130, 246, 0.15)' : '#374151',
                borderRadius: '8px',
                border: largeText ? '1px solid #3b82f6' : '1px solid transparent',
                transition: 'all 0.2s'
              }}>
                <input
                  type="checkbox"
                  checked={largeText}
                  onChange={(e) => {
                    setLargeText(e.target.checked);
                    if (e.target.checked) {
                      AccessibilityService.increaseFontSize();
                      AccessibilityService.increaseFontSize();
                    } else {
                      AccessibilityService.decreaseFontSize();
                      AccessibilityService.decreaseFontSize();
                    }
                  }}
                  style={{ marginRight: '0.75rem' }}
                />
                Large Text — make everything bigger
              </label>

              <label style={{
                display: 'flex',
                alignItems: 'center',
                color: '#f9fafb',
                cursor: 'pointer',
                padding: '0.75rem',
                background: highContrast ? 'rgba(59, 130, 246, 0.15)' : '#374151',
                borderRadius: '8px',
                border: highContrast ? '1px solid #3b82f6' : '1px solid transparent',
                transition: 'all 0.2s'
              }}>
                <input
                  type="checkbox"
                  checked={highContrast}
                  onChange={() => {
                    const newValue = AccessibilityService.toggleHighContrast();
                    setHighContrast(newValue);
                  }}
                  style={{ marginRight: '0.75rem' }}
                />
                High Contrast — bold colors for easier reading
              </label>
            </div>
          </div>

          {/* Get Started Button */}
          <div style={{ textAlign: 'right' }}>
            <button
              onClick={onGetStarted}
              style={{
                background: 'linear-gradient(135deg, #D4A017 0%, #B8860B 100%)',
                color: 'white',
                padding: '0.75rem 2rem',
                fontSize: '1rem',
                fontWeight: '600',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                boxShadow: '0 4px 14px 0 rgba(212, 160, 23, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 20px 0 rgba(212, 160, 23, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 14px 0 rgba(212, 160, 23, 0.3)';
              }}
            >
              GET STARTED
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WelcomeIntake;